import { useState, useRef, useCallback, type ReactNode } from 'react';
import { type PerkId } from '../game/systems/perks';
import { EVOLUTION_DEFS, type EvolutionId } from '../game/systems/evolutions';
import { useT, type TKey } from '../i18n';

const HOLD_MS = 400;

/**
 * Wraps a child with long-press detection. After 400ms pointerdown hold,
 * shows a fixed overlay with perk name + mechanic + flavour + (if applicable)
 * evolution pair hint. Tap below 400ms passes through to the child's onClick.
 */
export function PerkInfoTooltip({
  perkId,
  children,
}: {
  perkId: PerkId;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const timerRef = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const onPointerDown = useCallback(() => {
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      setOpen(true);
      timerRef.current = null;
    }, HOLD_MS);
  }, [clearTimer]);

  return (
    <>
      <div
        onPointerDown={onPointerDown}
        onPointerUp={clearTimer}
        onPointerLeave={clearTimer}
        onPointerCancel={clearTimer}
        onTouchMove={clearTimer}
      >
        {children}
      </div>
      {open && (
        <PerkInfoOverlay perkId={perkId} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

function PerkInfoOverlay({
  perkId,
  onClose,
}: {
  perkId: PerkId;
  onClose: () => void;
}) {
  const { t } = useT();
  const name = t(`perk.${perkId}.name` as TKey);
  const mechanic = t(`perk.${perkId}.mechanic` as TKey);
  const flavour = t(`perk.${perkId}.desc` as TKey);
  const pair = findEvolutionPair(perkId);

  return (
    <div
      role="dialog"
      aria-label={name}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 300,
        background: 'radial-gradient(80% 70% at 50% 100%, rgba(5,8,18,0.92), rgba(5,8,18,0.7))',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 360,
          padding: '20px 22px 22px',
          borderRadius: 14,
          background: 'linear-gradient(180deg, #1d2942 0%, #0a0f1c 100%)',
          border: '1px solid rgba(255,255,255,0.10)',
          boxShadow: '0 20px 50px -10px rgba(0,0,0,0.7)',
          color: '#fff',
        }}
      >
        <div style={{
          fontFamily: 'var(--f-display)',
          fontSize: 18,
          letterSpacing: '0.05em',
          marginBottom: 10,
        }}>{name}</div>
        <div style={{
          fontSize: 13,
          color: '#3dd5ff',
          marginBottom: 12,
          fontVariantNumeric: 'tabular-nums',
        }}>{mechanic}</div>
        <div style={{
          fontSize: 12,
          color: 'rgba(255,255,255,0.6)',
          fontStyle: 'italic',
          marginBottom: 12,
        }}>{flavour}</div>
        {pair && (
          <div style={{
            fontSize: 11,
            color: '#d8b3ff',
            paddingTop: 10,
            borderTop: '1px solid rgba(183,107,255,0.25)',
            letterSpacing: '0.04em',
          }}>
            Pairs with <strong>{t(`perk.${pair.partner}.name` as TKey)}</strong> →{' '}
            <strong>{t(`evolution.${pair.evolutionId}.shortName` as TKey)}</strong>
          </div>
        )}
      </div>
    </div>
  );
}

function findEvolutionPair(perkId: PerkId): { partner: PerkId; evolutionId: EvolutionId } | null {
  for (const evo of EVOLUTION_DEFS) {
    if (evo.pair[0] === perkId) return { partner: evo.pair[1], evolutionId: evo.id };
    if (evo.pair[1] === perkId) return { partner: evo.pair[0], evolutionId: evo.id };
  }
  return null;
}
